import { createSlice } from '@reduxjs/toolkit';
import * as actions from './actions';

interface TrackingLink {
  id: string;
  name: string;
  url: string;
  full_url: string;
  page_views: number;
  tickets_sold: number;
  sales: number;
}

interface LimitInfo {
  limit: number | 'unlimited';
  current_count: number;
  has_business_plus: boolean;
  remaining: number | 'unlimited';
}

interface TrackingLinksState {
  links: TrackingLink[];
  limitInfo: LimitInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: TrackingLinksState = {
  links: [],
  limitInfo: null,
  loading: false,
  error: null,
};

const trackingLinksSlice = createSlice({
  name: 'trackingLinks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch tracking links
    builder.addCase(actions.fetchTrackingLinks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(actions.fetchTrackingLinks.fulfilled, (state, action) => {
      state.loading = false;
      // Handle new API format: { links: [...], limit_info: {...} }
      if (action.payload.links) {
        state.links = action.payload.links;
        state.limitInfo = action.payload.limit_info;
      } else {
        // Fallback for old format (array)
        state.links = action.payload;
      }
    });
    builder.addCase(actions.fetchTrackingLinks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch tracking links';
    });

    // Create tracking link
    builder.addCase(actions.createTrackingLink.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.createTrackingLink.fulfilled, (state, action) => {
      state.loading = false;
      // Handle new API format: { links: [...], limit_info: {...} }
      if (action.payload.links) {
        state.links = action.payload.links;
        state.limitInfo = action.payload.limit_info;
      } else {
        // Fallback for old format (array)
        state.links = action.payload;
      }
    });
    builder.addCase(actions.createTrackingLink.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to create tracking link';
    });

    // Delete tracking link
    builder.addCase(actions.deleteTrackingLink.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.deleteTrackingLink.fulfilled, (state, action) => {
      state.loading = false;
      // Handle new API format: { links: [...], limit_info: {...} }
      if (action.payload.links) {
        state.links = action.payload.links;
        state.limitInfo = action.payload.limit_info;
      } else {
        // Fallback for old format (array)
        state.links = action.payload;
      }
    });
    builder.addCase(actions.deleteTrackingLink.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to delete tracking link';
    });
  },
});

export default trackingLinksSlice.reducer;
